import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Users, UserCheck, GraduationCap, Building2, BookOpen,
  Target, Award, Briefcase, TrendingUp, Ticket, DollarSign,
  ArrowUpRight, ArrowDownRight, Download
} from 'lucide-react';
import { exportToCSV } from '../../utils/exportUtils';

// ─── Animated counter ────────────────────────────────────────────────────────
const AnimatedNumber = ({ value, suffix = '' }) => {
  const display = value ?? 0;
  const num = typeof display === 'string' ? display : Math.round(display);
  return <>{num}{suffix}</>;
};

// ─── Single KPI card ─────────────────────────────────────────────────────────
const KPICard = ({ title, value, suffix = '', icon: Icon, trend, trendLabel, color, isDark, delay = 0, isFiltered = false }) => {
  const isPositive = trend > 0;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className={`relative overflow-hidden rounded-2xl p-5 border transition-all duration-300 hover:shadow-lg group ${
        isDark
          ? `bg-[#002147] border-[#1a3884]/50 hover:border-[#284BB3]/70 hover:shadow-[#284BB3]/10 ${isFiltered ? 'ring-1 ring-[#284BB3]/30' : ''}`
          : `bg-white border-gray-200 hover:border-gray-300 hover:shadow-md ${isFiltered ? 'ring-1 ring-blue-300' : ''}`
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-[10px] font-semibold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {title}
            {isFiltered && (
              <span className={`ml-1.5 px-1 py-0.5 rounded text-[8px] font-black ${isDark ? 'bg-[#1A3884] text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                FILTERED
              </span>
            )}
          </p>
          <p className={`text-3xl font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <AnimatedNumber value={value} suffix={suffix} />
          </p>
          {trend !== undefined && trend !== null && (
            <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${
              isPositive
                ? isDark ? 'text-emerald-400' : 'text-emerald-600'
                : isDark ? 'text-rose-400' : 'text-rose-600'
            }`}>
              {isPositive ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
              <span>{Math.abs(trend)}% {trendLabel || 'vs last period'}</span>
            </div>
          )}
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300 shadow-md"
          style={{ background: `linear-gradient(135deg, ${color}, ${color}99)` }}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}
      />
    </motion.div>
  );
};

// ─── KPIGrid ─────────────────────────────────────────────────────────────────
const KPIGrid = ({
  dashboardData,
  analyticsData,
  userStats,
  isDark,
  filters = {},
}) => {
  const isFiltered = !!(filters.college);

  // Resolve values: use college-specific data when filtered, global otherwise
  const kpis = useMemo(() => {
    const d = dashboardData || {};
    const a = analyticsData || {};
    const u = userStats || {};
    const isCollegeFiltered = !!filters.college;

    // Student counts
    const totalStudents = isCollegeFiltered
      ? (a.students?.total || 0)
      : (u.students || d.totalStudents || 0);
    const activeStudents = isCollegeFiltered
      ? (a.students?.active || 0)
      : (u.activeStudents || d.activeStudents || 0);

    // Global counts (not college-specific)
    const totalUsers = u.total || d.totalUsers || 0;
    const totalColleges = d.totalColleges || 0;
    const totalCourses = isCollegeFiltered
      ? (a.courses?.total || d.totalCourses || 0)
      : (d.totalCourses || 0);
    const totalAssessments = isCollegeFiltered
      ? (a.assessments?.total || 0)
      : (d.totalAssessments || 0);
    const totalRecruiters = u.recruiters || d.totalRecruiters || 0;

    // KPI calculations
    const placementPct = isCollegeFiltered && a.kpis ? Math.round(
      (a.kpis.courseCompletion || 0) * 0.4 +
      (a.assessments?.avgScore || 0) * 0.3 +
      (totalStudents > 0 ? (activeStudents / totalStudents) * 100 : 0) * 0.3
    ) : (d.placementRate || 0);

    // Ticket resolution % - always global (from dashboard)
    const ticketResolution = d.ticketResolutionRate || 0;

    // Revenue
    const revenue = d.revenue || null;

    return {
      totalUsers,
      activeStudents,
      totalStudents,
      totalColleges,
      totalCourses,
      totalAssessments,
      totalRecruiters,
      placementPct,
      ticketResolution,
      revenue,
    };
  }, [dashboardData, analyticsData, userStats, filters.college]);

  const handleExportCSV = () => {
    const rows = [{
      'Total Users': kpis.totalUsers,
      'Total Students': kpis.totalStudents,
    }];
    exportToCSV(rows, Object.keys(rows[0]).map(k => ({ header: k, accessor: k })), 'KPI_Summary');
  };

  const cards = [
    { title: 'Total Users', value: kpis.totalUsers, icon: Users, color: '#284BB3' },
    { title: 'Students', value: kpis.totalStudents, icon: GraduationCap, color: '#3B82F6', isFiltered: true },
  ];

  if (!isFiltered) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          {isFiltered && (
            <p className={`text-xs font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              Cards marked FILTERED show college-specific values
            </p>
          )}
        </div>
        <button
          onClick={handleExportCSV}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
            isDark ? 'border-[#1a3884]/50 text-gray-300 hover:bg-[#1a3884]/20' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Download size={12} />
          Export KPIs
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cards.map((card, i) => (
          <KPICard
            key={card.title}
            {...card}
            isDark={isDark}
            delay={i * 0.04}
            isFiltered={card.isFiltered && isFiltered}
          />
        ))}
      </div>
    </div>
  );
};

export default KPIGrid;
